import * as React from "react"

import { cn } from "@/lib/utils"

export interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, onChange, onBlur, ...props }, ref) => {
    const internalRef = React.useRef<HTMLTextAreaElement>(null);
    const combinedRef = (node: HTMLTextAreaElement | null) => {
      internalRef.current = node;
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    };

    // Safari mobile fix: ensure onChange fires properly
    const handleChange = React.useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
      onChange?.(e);
    }, [onChange]);

    // Safari mobile fix: trigger change on blur if value changed
    const handleBlur = React.useCallback((e: React.FocusEvent<HTMLTextAreaElement>) => {
      // Force a final change event on blur for Safari
      if (onChange && internalRef.current) {
        const syntheticEvent = {
          ...e,
          target: internalRef.current,
          currentTarget: internalRef.current,
        } as React.ChangeEvent<HTMLTextAreaElement>;
        onChange(syntheticEvent);
      }
      onBlur?.(e);
    }, [onChange, onBlur]);

    return (
      <textarea
        className={cn(
          "flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
          // Mobile Safari specific fixes
          "appearance-none",
          className
        )}
        ref={combinedRef}
        onChange={handleChange}
        onBlur={handleBlur}
        // Prevent iOS zoom on focus
        style={{ fontSize: '16px', ...props.style }}
        {...props}
      />
    )
  }
)
Textarea.displayName = "Textarea"

export { Textarea }
